import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Paper,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import axios from "axios";

const primaryColor = "#005EB8";

// KPI Card colors
const kpiColors = {
  totalDemand: "#E53935",       // Red
  internalSupply: "#43A047",    // Green
  externalSupply: "#1E88E5",    // Blue
  skillTowers: "#8E24AA",       // Purple
  pdp: "#FB8C00",               // Orange
  vcdp: "#00ACC1",              // Cyan
  rotation: "#5E35B1",          // Deep Purple
  lateralHiring: "#D81B60",     // Pink
};

// Colors for pie charts
const PIE_COLORS_PDP = ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF"];
const PIE_COLORS_VCDP = ["#FF9F40", "#4BC0C0", "#9966FF", "#FF6384", "#36A2EB"];
const DOUGHNUT_COLORS = [kpiColors.totalDemand, kpiColors.internalSupply, kpiColors.externalSupply];

// KPI Card Component
const KPICard = ({ title, value, color }) => (
  <Paper
    elevation={3}
    sx={{
      p: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 100,
      borderTop: `4px solid ${color}`,
      transition: "transform 0.2s, box-shadow 0.2s",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: 6,
      },
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: "center" }}>
      {title}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: "bold", color }}>
      {value}
    </Typography>
  </Paper>
);

// Custom label for pie chart
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return percent > 0.05 ? (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  ) : null;
};

function DashboardCharts() {
  const [loading, setLoading] = useState(false);
  const [practice, setPractice] = useState("All");
  const [market, setMarket] = useState("All");
  const [offOn, setOffOn] = useState("All");

  // KPI state
  const [kpis, setKpis] = useState({
    totalDemand: 0,
    internalSupply: 0,
    externalSupply: 0,
    skillTowers: 0,
    pdp: 0,
    vcdp: 0,
    rotation: 0,
    lateralHiring: 0,
  });

  // Chart data state
  const [barChartData, setBarChartData] = useState([]);
  const [doughnutData, setDoughnutData] = useState([]);
  const [pdpPieData, setPdpPieData] = useState([]);
  const [vcdpPieData, setVcdpPieData] = useState([]);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_API_BASE_URL + "/dashboard/report",
        {
          params: { practice, market, offOn },
        }
      );
      const data = response.data || [];
      console.log("Dashboard data received:", data);
      if (data.length > 0) {
        console.log("First row keys:", Object.keys(data[0]));
        console.log("Last row (Grand Total):", data[data.length - 1]);
      }
      calculateKPIs(data);
      prepareChartData(data);
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateKPIs = (data) => {
    if (!data || data.length === 0) return;

    // Get all keys from first row to understand structure
    const sampleRow = data[0];
    const keys = Object.keys(sampleRow);
    
    // Find the skill tower key (first column)
    const skillTowerKey = keys.find(k => k.toLowerCase().includes("skill")) || keys[0];
    
    // Last row is the Grand Total
    const grandTotalRow = data[data.length - 1];
    
    // Filter out the "Grand Total" row for skill towers count
    const dataRows = data.filter((row, index) => index < data.length - 1);

    console.log("Skill tower key:", skillTowerKey);
    console.log("Grand total row:", grandTotalRow);

    // Dynamically find field names (case-insensitive)
    const findKey = (row, searchTerms) => {
      const rowKeys = Object.keys(row);
      for (const term of searchTerms) {
        const found = rowKeys.find(k => k.toLowerCase().replace(/[_\s]/g, "").includes(term.toLowerCase().replace(/[_\s]/g, "")));
        if (found) return found;
      }
      return null;
    };

    const demandKey = findKey(grandTotalRow, ["total_demand", "totaldemand"]);
    const internalSupplyKey = findKey(grandTotalRow, ["total_internal_supply", "totalinternalsupply", "internal_supply"]);
    const externalSupplyKey = findKey(grandTotalRow, ["total_external_supply", "totalexternalsupply", "external_supply"]);
    const pdpKey = findKey(grandTotalRow, ["pdp"]);
    const vcdpKey = findKey(grandTotalRow, ["vcdp"]);
    const rotationKey = findKey(grandTotalRow, ["rotation"]);
    const lateralCwrKey = findKey(grandTotalRow, ["lateral_hiring_cwr", "lateralhiringcwr"]);
    const lateralFteKey = findKey(grandTotalRow, ["lateral_hiring_fte", "lateralhiringfte"]);

    console.log("Found keys:", { demandKey, internalSupplyKey, externalSupplyKey, pdpKey, vcdpKey, rotationKey, lateralCwrKey, lateralFteKey });

    const totalDemand = demandKey ? Number(grandTotalRow[demandKey]) || 0 : 0;
    const internalSupply = internalSupplyKey ? Number(grandTotalRow[internalSupplyKey]) || 0 : 0;
    const externalSupply = externalSupplyKey ? Number(grandTotalRow[externalSupplyKey]) || 0 : 0;
    const pdpTotal = pdpKey ? Number(grandTotalRow[pdpKey]) || 0 : 0;
    const vcdpTotal = vcdpKey ? Number(grandTotalRow[vcdpKey]) || 0 : 0;
    const rotation = rotationKey ? Number(grandTotalRow[rotationKey]) || 0 : 0;
    const lateralHiringCWR = lateralCwrKey ? Number(grandTotalRow[lateralCwrKey]) || 0 : 0;
    const lateralHiringFTE = lateralFteKey ? Number(grandTotalRow[lateralFteKey]) || 0 : 0;

    console.log("KPI values:", { totalDemand, internalSupply, externalSupply, pdpTotal, vcdpTotal, rotation, lateralHiringCWR, lateralHiringFTE });

    setKpis({
      totalDemand,
      internalSupply,
      externalSupply,
      skillTowers: dataRows.length,
      pdp: pdpTotal,
      vcdp: vcdpTotal,
      rotation,
      lateralHiring: lateralHiringCWR + lateralHiringFTE,
    });

    // Doughnut data
    setDoughnutData([
      { name: "Total Demand", value: totalDemand },
      { name: "Internal Supply", value: internalSupply },
      { name: "External Supply", value: externalSupply },
    ]);
  };

  const prepareChartData = (data) => {
    if (!data || data.length === 0) return;

    // Get keys from first row
    const sampleRow = data[0];
    const keys = Object.keys(sampleRow);
    
    // Find field keys dynamically
    const findKey = (row, searchTerms) => {
      const rowKeys = Object.keys(row);
      for (const term of searchTerms) {
        const found = rowKeys.find(k => k.toLowerCase().replace(/[_\s]/g, "").includes(term.toLowerCase().replace(/[_\s]/g, "")));
        if (found) return found;
      }
      return null;
    };

    const skillTowerKey = findKey(sampleRow, ["skill_tower", "skilltower", "skill"]) || keys[0];
    const demandKey = findKey(sampleRow, ["total_demand", "totaldemand"]);
    const internalSupplyKey = findKey(sampleRow, ["total_internal_supply", "totalinternalsupply"]);
    const externalSupplyKey = findKey(sampleRow, ["total_external_supply", "totalexternalsupply"]);
    const pdpKey = findKey(sampleRow, ["pdp"]);
    const vcdpKey = findKey(sampleRow, ["vcdp"]);

    // Filter out grand total row (last row)
    const dataRows = data.slice(0, -1);

    // Sort by total demand and get top 5
    const sorted = [...dataRows].sort(
      (a, b) => (Number(b[demandKey]) || 0) - (Number(a[demandKey]) || 0)
    );
    const top5 = sorted.slice(0, 5);

    // Bar chart data
    setBarChartData(
      top5.map((row) => ({
        name: row[skillTowerKey] || "Unknown",
        demand: Number(row[demandKey]) || 0,
        supply:
          (Number(row[internalSupplyKey]) || 0) +
          (Number(row[externalSupplyKey]) || 0),
      }))
    );

    // PDP breakdown by skill tower (top 5 by PDP)
    const pdpSorted = [...dataRows]
      .filter((row) => pdpKey && Number(row[pdpKey]) > 0)
      .sort((a, b) => (Number(b[pdpKey]) || 0) - (Number(a[pdpKey]) || 0))
      .slice(0, 5);

    setPdpPieData(
      pdpSorted.map((row) => ({
        name: row[skillTowerKey] || "Unknown",
        value: Number(row[pdpKey]) || 0,
      }))
    );

    // VCDP breakdown by skill tower (top 5 by VCDP)
    const vcdpSorted = [...dataRows]
      .filter((row) => vcdpKey && Number(row[vcdpKey]) > 0)
      .sort((a, b) => (Number(b[vcdpKey]) || 0) - (Number(a[vcdpKey]) || 0))
      .slice(0, 5);

    setVcdpPieData(
      vcdpSorted.map((row) => ({
        name: row[skillTowerKey] || "Unknown",
        value: Number(row[vcdpKey]) || 0,
      }))
    );
  };

  const handleViewReport = () => {
    fetchReportData();
  };

  return (
    <Box sx={{ width: "100%", p: 1 }}>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="practice-label">Practice</InputLabel>
            <Select
              labelId="practice-label"
              value={practice}
              label="Practice"
              onChange={(e) => setPractice(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="EPS PEGA">EPS PEGA</MenuItem>
              <MenuItem value="EPS IPM">EPS IPM</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="market-label">Region</InputLabel>
            <Select
              labelId="market-label"
              value={market}
              label="Region"
              onChange={(e) => setMarket(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="NA">NA</MenuItem>
              <MenuItem value="GGM">GGM</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="off-on-label">Off/On</InputLabel>
            <Select
              labelId="off-on-label"
              value={offOn}
              label="Off/On"
              onChange={(e) => setOffOn(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="onsite">onsite</MenuItem>
              <MenuItem value="offshore">offshore</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            onClick={handleViewReport}
            disabled={loading}
          >
            {loading ? "Loading..." : "View Report"}
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3} md={1.5}>
              <KPICard
                title="Total Demand"
                value={kpis.totalDemand}
                color={kpiColors.totalDemand}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <KPICard
                title="Internal Supply"
                value={kpis.internalSupply}
                color={kpiColors.internalSupply}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <KPICard
                title="External Supply"
                value={kpis.externalSupply}
                color={kpiColors.externalSupply}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <KPICard
                title="Skill Towers"
                value={kpis.skillTowers}
                color={kpiColors.skillTowers}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <KPICard
                title="Total PDP"
                value={kpis.pdp}
                color={kpiColors.pdp}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <KPICard
                title="Total VCDP"
                value={kpis.vcdp}
                color={kpiColors.vcdp}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <KPICard
                title="Rotation"
                value={kpis.rotation}
                color={kpiColors.rotation}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <KPICard
                title="Lateral Hiring"
                value={kpis.lateralHiring}
                color={kpiColors.lateralHiring}
              />
            </Grid>
          </Grid>

          {/* Charts in 2x2 Grid */}
          <Grid container spacing={2}>
            {/* Bar Chart - Top 5 Skill Towers */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: 380 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}
                >
                  Top 5 Skill Towers - Demand vs Supply
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={barChartData} margin={{ top: 10, right: 30, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      angle={-35}
                      textAnchor="end"
                      interval={0}
                      height={80}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ paddingTop: 10 }} />
                    <Bar dataKey="demand" name="Total Demand" fill={kpiColors.totalDemand} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="supply" name="Total Supply" fill={kpiColors.internalSupply} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Doughnut Chart - Demand vs Supply */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: 380 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}
                >
                  Demand vs Supply Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={doughnutData}
                      cx="50%"
                      cy="45%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      labelLine={false}
                      label={renderCustomizedLabel}
                    >
                      {doughnutData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={DOUGHNUT_COLORS[index % DOUGHNUT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => value.toLocaleString()} />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* PDP Pie Chart */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: 380 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}
                >
                  PDP by Skill Tower (Top 5)
                </Typography>
                {pdpPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={pdpPieData}
                        cx="50%"
                        cy="45%"
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        labelLine={false}
                        label={renderCustomizedLabel}
                      >
                        {pdpPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS_PDP[index % PIE_COLORS_PDP.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      height: 320,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography color="text.secondary">No PDP data available</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* VCDP Pie Chart */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: 380 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 1, textAlign: "center" }}
                >
                  VCDP by Skill Tower (Top 5)
                </Typography>
                {vcdpPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={vcdpPieData}
                        cx="50%"
                        cy="45%"
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        labelLine={false}
                        label={renderCustomizedLabel}
                      >
                        {vcdpPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS_VCDP[index % PIE_COLORS_VCDP.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => value.toLocaleString()} />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      height: 320,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography color="text.secondary">No VCDP data available</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

export default DashboardCharts;
